import { tokenSwap, createArcClient, getOutreachToken, prepareRegistration, submitRegistration } from './accessToken'
import { AuthMigrationError, errorDetails, requestError } from './googleMigration'
import Cookies from 'js-cookie'
import axios from 'axios'
import store from '@/store'
import { bus } from './event-bus'

const MAX_ATTEMPTS = 3
let readiness = null
let active = null
let readySession = null
let accountCheck = null
let generation = 0
let vueInstance = null
let registrationWork = null

export function createAuthReadyPromise () {
  if (!readiness) {
    const deferred = { settled: false }
    deferred.promise = new Promise((resolve, reject) => {
      deferred.resolve = () => { deferred.settled = true; resolve() }
      deferred.reject = error => { deferred.settled = true; reject(error) }
    })
    // There may not be a route guard waiting yet when initialization fails.
    deferred.promise.catch(() => {})
    readiness = deferred
  }
  return readiness.promise
}

function sessionKey (auth) {
  if (!auth || !auth.isAuthenticated || !auth.user || !auth.idTokenClaims) return null
  return JSON.stringify([auth.user.sub, auth.idTokenClaims.__raw, auth.idTokenClaims.iat, auth.idTokenClaims.auth_time])
}

function clearAccountData () {
  store.commit('auth/setProfile', null)
  store.commit('auth/setProfilePicture', null)
  store.dispatch('form/clearData')
  store.commit('form/setLoadingData', false)
  store.commit('table/setTableData', {})
}

export function resetAuth (Vue = vueInstance) {
  generation++
  if (readiness && !readiness.settled) {
    readiness.reject(new AuthMigrationError('SESSION_CHANGED', 'The sign-in session changed. Please try again.', { action: 'google-login' }))
  }
  readiness = null
  active = null
  readySession = null
  accountCheck = null
  registrationWork = null
  vueInstance = Vue
  store.dispatch('auth/resetAuthState')
  clearAccountData()
  if (Vue) {
    Vue.prototype.$arc = null
    Vue.prototype.$outreach = null
  }
}

export function initializeAuth (Vue) {
  vueInstance = Vue
  const auth = Vue.prototype.$auth
  const key = sessionKey(auth)
  if (key && key === readySession && Vue.prototype.$arc) return Promise.resolve(true)
  if (active && active.key === key) return active.promise
  if (active || readySession) resetAuth(Vue)
  if (readiness && readiness.settled) readiness = null
  createAuthReadyPromise()
  const deferred = readiness
  const epoch = generation
  if (!accountCheck || accountCheck.key !== key) accountCheck = { key, baselineState: {} }
  const context = auth && { user: auth.user, claims: auth.idTokenClaims, freshLogin: auth.freshLogin, accountCheck: accountCheck.baselineState }
  const assertCurrent = () => {
    if (epoch !== generation || key !== sessionKey(Vue.prototype.$auth)) {
      throw new AuthMigrationError('SESSION_CHANGED', 'The sign-in session changed. Please try again.', { action: 'google-login' })
    }
  }
  store.commit('auth/setAuthReady', false)
  store.commit('auth/setAuthInitializing', true)
  store.commit('auth/setAuthError', null)

  const promise = (async () => {
    try {
      if (!key || auth.loading || auth.error) {
        throw auth?.error || new AuthMigrationError('LOGIN_REQUIRED', 'Sign in with Google to continue.', { action: 'google-login' })
      }
      let user
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        try {
          assertCurrent()
          user = await tokenSwap(context, assertCurrent)
          break
        } catch (error) {
          const safe = requestError(error)
          if (!safe.retryable || attempt === MAX_ATTEMPTS - 1) throw safe
          await new Promise(resolve => setTimeout(resolve, 2000 * Math.pow(2, attempt)))
        }
      }
      assertCurrent()
      const previousKey = Cookies.get('_.arc_score.arcScoreKey')
      let outreachKey = previousKey === user.api_key ? Cookies.get('_.arc_score.salesOutreachKey') : null
      if (process.env.NODE_ENV !== 'everservice' && !outreachKey) {
        outreachKey = await getOutreachToken(user.ms_graph_id || user.google_id)
      }
      assertCurrent()
      clearAccountData()
      // Publish credentials only after the entire ARC account check has succeeded.
      Cookies.set('_.arc_score.arcScoreKey', user.api_key)
      Cookies.set('_.arc_score.isGuest', false)
      if (outreachKey && process.env.NODE_ENV !== 'everservice') {
        Cookies.set('_.arc_score.salesOutreachKey', outreachKey)
      } else {
        Cookies.remove('_.arc_score.salesOutreachKey')
      }
      Vue.prototype.$arc = createArcClient(user.api_key)
      Vue.prototype.$outreach = outreachKey && process.env.NODE_ENV !== 'everservice'
        ? axios.create({
          baseURL: process.env.VUE_APP_SALES_OUTREACH_API,
          timeout: 15000,
          headers: { 'Content-Type': 'application/json', Authorization: `Token ${outreachKey}` }
        })
        : null
      store.commit('auth/setOutreachKey', !!Vue.prototype.$outreach)
      readySession = key
      store.commit('auth/setAuthReady', true)
      store.commit('auth/setAuthInitializing', false)
      deferred.resolve()
      bus.$emit('token-swap')
      return true
    } catch (error) {
      const safe = requestError(error)
      if (epoch === generation) {
        readySession = null
        Vue.prototype.$arc = null
        Vue.prototype.$outreach = null
        store.commit('auth/setAuthReady', false)
        store.commit('auth/setAuthInitializing', false)
        store.commit('auth/setAuthError', errorDetails(safe))
        deferred.reject(safe)
      }
      throw safe
    }
  })()
  active = { key, promise }
  // Do not let an old login clear a newer login's pending operation.
  promise.then(() => { if (active?.promise === promise) active = null }, () => { if (active?.promise === promise) active = null })
  return promise
}

export function waitForAuth () {
  if (readySession && store.state.auth.authReady) return Promise.resolve()
  if (store.state.auth.authError) return Promise.reject(store.state.auth.authError)
  return createAuthReadyPromise()
}

export function isAuthReady () {
  return !!readySession && store.state.auth.authReady
}

function registrationOperation (Vue, kind, operation) {
  const auth = Vue.prototype.$auth
  const key = sessionKey(auth)
  if (!key || auth.loading || auth.error) return Promise.reject(new AuthMigrationError('LOGIN_REQUIRED', 'Sign in with Google to continue.', { action: 'google-login' }))
  if (registrationWork?.key === key) {
    return registrationWork.kind === kind ? registrationWork.promise : Promise.reject(new AuthMigrationError('REGISTRATION_BUSY', 'Please wait for the current account check to finish.', { action: 'retry' }))
  }
  const epoch = generation
  if (!accountCheck || accountCheck.key !== key) accountCheck = { key, baselineState: {} }
  const context = { user: auth.user, claims: auth.idTokenClaims, freshLogin: auth.freshLogin, accountCheck: accountCheck.baselineState }
  const assertCurrent = () => {
    if (epoch !== generation || key !== sessionKey(Vue.prototype.$auth)) {
      throw new AuthMigrationError('SESSION_CHANGED', 'Your sign-in changed. Sign in again to continue.', { action: 'google-login' })
    }
  }
  const promise = Promise.resolve().then(() => { assertCurrent(); return operation(context, assertCurrent) })
  registrationWork = { key, kind, promise }
  const clear = () => { if (registrationWork?.promise === promise) registrationWork = null }
  promise.then(clear, clear)
  return promise
}

export function inspectRegistration (Vue) {
  return registrationOperation(Vue, 'inspect', async (context, assertCurrent) => {
    const result = await prepareRegistration(context, assertCurrent)
    assertCurrent()
    if (result.account) await initializeAuth(Vue)
    return result
  })
}

export function registerAccount (Vue, details) {
  return registrationOperation(Vue, 'create', async (context, assertCurrent) => {
    await submitRegistration(context, assertCurrent, details)
    assertCurrent()
    return initializeAuth(Vue)
  })
}
